/*
 * ニュース表示の処理。
 *
 * 1. data 属性から一覧・詳細・トップのニュース領域を判定する。
 * 2. API取得中は skeleton.js の共通部品で作るスケルトンを表示する。
 * 3. APIの取得後、既存デザインに沿ったニュース専用HTMLへ差し替える。
 * 4. トップのスライダーだけは、要素差し替え後にSwiperを初期化する。
 *
 * skeleton.js は汎用部品のみを担当し、このファイルはニュース固有の構造を担当する。
 */
(function () {
  // 外部WordPress側でTRYANGLE-WEB向けニュースを判別するためのサイトID。
  const NEWS_SITE_ID = 215;
  // アイキャッチ未設定時に表示する、このサイト内の代替画像。
  const DEFAULT_IMAGE = "/assets/img/no-image.webp";
  // 本番サイトで統一する正規ホスト。ニュースのクエリ付きURLにも使用する。
  const CANONICAL_ORIGIN = "https://www.tryangle-web.com";
  // .env の NEWS_PROXY_BASE_URL がビルド時に入る。未設定なら自サイト内APIを使う。
  const NEWS_PROXY_BASE_URL = "";
  // skeleton.js で公開した、サイト共通のスケルトン部品。
  const Skeleton = window.SkeletonLoader;

  if (!Skeleton) {
    // eslint-disable-next-line no-console
    console.error("SkeletonLoader が読み込まれていません。");
    return;
  }

  // ローカルの /dist/ プレビューと公開URLの両方で動かすため、
  // API・アセットの起点を最初に一度だけ算出して保持する。
  const state = {
    apiBase: getApiBase(),
    rootPrefix: getRootPrefix(),
  };

  // --- ページ種別ごとの初期化 --------------------------------------------
  document.addEventListener("DOMContentLoaded", () => {
    // ページにある data 属性を見て、必要なニュース表示だけを実行する。
    // 同じJSを全ページで読み込んでも、対象のないページでは何もしない。
    const indexRoot = document.querySelector("[data-news-index]");
    const detailRoot = document.querySelector("[data-news-detail]");
    const homeRoot = document.querySelector("[data-home-news]");

    if (indexRoot) {
      renderNewsIndex(indexRoot);
    }

    if (detailRoot) {
      renderNewsDetail(detailRoot);
    }

    if (homeRoot) {
      renderHomeNews(homeRoot);
    }
  });

  // --- 一覧ページ ----------------------------------------------------------
  async function renderNewsIndex(root) {
    // ニュース一覧ページ: クエリの paged を受け取り、9件ずつ表示する。
    const list = root.querySelector("[data-news-list]");
    const pager = root.querySelector("[data-news-pager]");
    if (!list) {
      return;
    }

    const paged = getPositiveInt(new URLSearchParams(window.location.search).get("paged"), 1);
    // 2ページ目以降はページ番号を含め、一覧ページの正規URLを現在の表示内容と一致させる。
    updateCanonicalUrl(paged > 1 ? `/news/?paged=${paged}` : "/news/");
    // 通信完了まで先にスケルトンを描画し、表示領域の高さを確保する。
    list.innerHTML = renderArchiveSkeleton(9);
    if (pager) {
      pager.hidden = true;
      pager.innerHTML = "";
    }

    try {
      const data = await fetchJson(`${state.apiBase}/list.php?paged=${paged}&per_page=9`);
      if (!data.ok) {
        throw new Error(data.error?.message || "News API error");
      }

      // 取得した記事とページャーを、モックと同じHTML構造へ差し替える。
      list.innerHTML = data.items.map(renderArchiveItem).join("");
      if (pager) {
        pager.innerHTML = renderPager(data.pagination);
        pager.hidden = data.pagination.totalPages <= 1;
      }
    } catch (error) {
      // 一覧だけは、失敗時にページャーを出さずエラー表示へ切り替える。
      // eslint-disable-next-line no-console
      console.error(error);
      list.innerHTML = renderError("ニュースを読み込めませんでした。");
    }
  }

  // --- 詳細ページ ----------------------------------------------------------
  async function renderNewsDetail(root) {
    // ニュース詳細ページ: URLの pid を使って該当記事を取得する。
    const params = new URLSearchParams(window.location.search);
    const pid = getPositiveInt(params.get("pid"), 0);
    if (!pid) {
      return;
    }
    // 記事IDを含む詳細URLを正規URLとして反映する。
    updateCanonicalUrl(`/news/detail.php?pid=${pid}`);

    const title = root.querySelector("[data-news-title]");
    const date = root.querySelector("[data-news-date]");
    const tags = root.querySelector("[data-news-tags]");
    const body = root.querySelector("[data-news-body]");

    // タイトル、日付、タグ、本文をそれぞれスケルトンに置き換え、
    // 記事取得中にもページのレイアウトが大きく動かないようにする。
    if (title) {
      title.innerHTML = renderTitleSkeleton();
    }
    if (date) {
      date.innerHTML = '<span class="c_skeleton c_skeleton_meta"></span>';
    }
    if (tags) {
      tags.innerHTML = '<span class="c_skeleton c_skeleton_tag"></span><span class="c_skeleton c_skeleton_tag"></span>';
    }
    if (body) {
      body.innerHTML = renderEntrySkeleton();
    }

    try {
      const data = await fetchJson(`${state.apiBase}/detail.php?pid=${pid}`);
      if (!data.ok) {
        throw new Error(data.error?.message || "News API error");
      }

      // APIから受け取った記事データを、各表示領域へ反映する。
      const item = data.item;
      if (title) {
        title.textContent = item.title;
      }
      if (date) {
        date.textContent = item.date;
      }
      if (tags) {
        tags.innerHTML = renderTags(item.tags);
      }
      if (body) {
        body.innerHTML = item.body || "";
      }
      // クライアント描画後もページタイトルを記事名に更新する。
      document.title = item.title ? `${item.title} | tryangle-web.com` : document.title;
    } catch (error) {
      // 記事を取得できない場合は、タイトルと本文に状態が分かる内容を表示する。
      // eslint-disable-next-line no-console
      console.error(error);
      if (title) {
        title.textContent = "ニュースを読み込めませんでした";
      }
      if (body) {
        body.innerHTML = renderError("時間をおいて再度お試しください。");
      }
    }
  }

  // --- トップページ --------------------------------------------------------
  async function renderHomeNews(root) {
    // トップページ: 1回のAPI取得で7件受け取り、
    // 先頭4件をスライダー、続く3件を通常一覧へ振り分ける。
    const sliderList = root.querySelector("[data-home-news-slider-list]");
    const list = root.querySelector("[data-home-news-list]");
    if (!sliderList && !list) {
      return;
    }

    // 通信中も、実際の件数と近い見た目・高さを保つ。
    if (sliderList) {
      sliderList.innerHTML = renderHomeSkeletonSlides(4);
    }
    if (list) {
      list.innerHTML = renderListSkeleton(3);
    }

    try {
      const data = await fetchJson(`${state.apiBase}/list.php?paged=1&per_page=7`);
      if (!data.ok) {
        throw new Error(data.error?.message || "News API error");
      }

      // API呼び出しを増やさず、受け取った7件を画面内の2エリアに分割する。
      const sliderItems = data.items.slice(0, 4);
      const listItems = data.items.slice(4, 7);

      if (sliderList) {
        sliderList.innerHTML = sliderItems.map(renderHomeSlide).join("");
      }
      if (list) {
        list.innerHTML = listItems.map(renderHomeListItem).join('<hr class="c_hr -gray02" />');
      }

      // 動的にスライド要素を入れた後でSwiperを初期化する。
      // top-page.jsもこのイベントを監視しているため、二重に呼んでも安全。
      document.dispatchEvent(new CustomEvent("tryangle:homeNewsRendered"));
      if (typeof window.tryangleInitNewsSlider === "function") {
        window.tryangleInitNewsSlider();
      }
    } catch (error) {
      // スライダーは空にせずスケルトンを維持し、下段一覧だけエラーを明示する。
      // eslint-disable-next-line no-console
      console.error(error);
      if (sliderList) {
        sliderList.innerHTML = renderHomeSkeletonSlides(4);
      }
      if (list) {
        list.innerHTML = renderError("ニュースを読み込めませんでした。");
      }
      document.dispatchEvent(new CustomEvent("tryangle:homeNewsRendered"));
    }
  }

  // --- 実データのHTML生成 --------------------------------------------------
  // APIのデータを、各ページの既存デザインに合わせたHTMLへ変換する。
  function renderArchiveItem(item) {
    const image = item.thumbnail || {};
    const imageAlt = image.alt || item.title || "";
    const imageWidth = Number.parseInt(image.width, 10) || 855;
    const imageHeight = Number.parseInt(image.height, 10) || 855;
    return `
      <a href="${escapeAttr(makeDetailUrl(item.id))}" class="news_item c_news_item">
        <div class="img_area">
          <div class="img_fit">
            <img src="${escapeAttr(getImageUrl(image.url))}" alt="${escapeAttr(imageAlt)}" width="${imageWidth}" height="${imageHeight}" loading="lazy" decoding="async" />
          </div>
        </div>
        <div class="txt_area">
          <div class="c_cat_list">${renderTags(item.tags)}</div>
          <div class="date_ttl_wrap">
            <div class="c_date">${escapeHtml(item.date)}</div>
            <div class="title">${escapeHtml(item.title)}</div>
          </div>
        </div>
      </a>
    `;
  }

  function renderHomeSlide(item) {
    // Swiperが認識できる .swiper-slide を含めて、トップ用カードを生成する。
    const image = item.thumbnail || {};
    const imageAlt = image.alt || item.title || "";
    const imageWidth = Number.parseInt(image.width, 10) || 855;
    const imageHeight = Number.parseInt(image.height, 10) || 855;
    return `
      <div class="swiper-slide">
        <a href="${escapeAttr(makeDetailUrl(item.id))}" class="news_slider_item c_news_item">
          <div class="img_area">
            <div class="img_fit">
              <img src="${escapeAttr(getImageUrl(image.url))}" alt="${escapeAttr(imageAlt)}" width="${imageWidth}" height="${imageHeight}" loading="lazy" decoding="async" />
            </div>
          </div>
          <div class="txt_area">
            <div class="c_cat_list">${renderTags(item.tags)}</div>
            <div class="date_ttl_wrap">
              <div class="c_date">${escapeHtml(item.date)}</div>
              <div class="title">${escapeHtml(item.title)}</div>
            </div>
          </div>
        </a>
      </div>
    `;
  }

  function renderHomeListItem(item) {
    // トップ下段は画像・タグを省いた、日付とタイトルだけの簡易一覧にする。
    return `
      <a href="${escapeAttr(makeDetailUrl(item.id))}" class="news_item">
        <div class="c_date">${escapeHtml(item.date)}</div>
        <div class="ttl">${escapeHtml(item.title)}</div>
      </a>
    `;
  }

  function renderError(text) {
    // APIエラー時に、空白ではなく利用者に状態が伝わる表示を返す。
    return `
      <div class="c_news_error" role="alert">
        <span>${escapeHtml(text)}</span>
      </div>
    `;
  }

  // --- ニュース専用スケルトンのHTML生成 ----------------------------------
  // c_news_skeleton_* はニュース専用のレイアウト、
  // Skeleton.* は skeleton.js から使う汎用の行・画像・タグ部品。
  function renderArchiveSkeleton(count) {
    // skeleton.js の繰り返し処理で、ニュースカードを指定数だけ並べる。
    return Skeleton.repeat(count, renderCardSkeleton);
  }

  function renderHomeSkeletonSlides(count) {
    // 実データと同じく .swiper-slide で包み、初期化前の高さを安定させる。
    return Skeleton.repeat(count, () => `<div class="swiper-slide">${renderCardSkeleton()}</div>`);
  }

  function renderListSkeleton(count) {
    // 下段一覧と同じ位置に罫線を入れ、読み込み中も完成時の間隔を再現する。
    return Skeleton.repeat(
      count,
      (index) => `${index > 0 ? '<hr class="c_hr -gray02" />' : ""}${renderListItemSkeleton()}`,
    );
  }

  function renderCardSkeleton() {
    // 一覧とトップスライダーで共有する、ニュースカード用の骨組み。
    return `
      <div class="c_news_skeleton_card" aria-hidden="true">
        ${Skeleton.image({ tagName: "div" })}
        <div class="c_news_skeleton_body">
          <div class="c_news_skeleton_tags">
            ${Skeleton.tag()}
            ${Skeleton.tag()}
          </div>
          ${Skeleton.meta()}
          ${Skeleton.line("long")}
          ${Skeleton.line("medium")}
        </div>
      </div>
    `;
  }

  function renderListItemSkeleton() {
    // トップ下段の簡易一覧用の骨組み。
    return `
      <div class="c_news_skeleton_list_item" aria-hidden="true">
        ${Skeleton.meta()}
        ${Skeleton.line("long")}
        ${Skeleton.line("medium")}
      </div>
    `;
  }

  function renderEntrySkeleton() {
    // 詳細本文用の骨組み。画像比率・余白はニュース専用SCSSで定義する。
    return `
      <div class="c_news_skeleton_entry" aria-hidden="true">
        ${Skeleton.image()}
        ${Skeleton.line("long")}
        ${Skeleton.line("long")}
        ${Skeleton.line("medium")}
        ${Skeleton.line("long")}
        ${Skeleton.line("short")}
      </div>
    `;
  }

  function renderTitleSkeleton() {
    // 詳細タイトルは2行分を先に確保し、記事名の表示時にレイアウトを安定させる。
    return `
      ${Skeleton.line("long")}
      ${Skeleton.line("medium")}
    `;
  }

  function renderTags(tags) {
    // タグがない記事ではタグエリアを空にし、ある場合だけクラス・色を反映する。
    if (!Array.isArray(tags) || tags.length === 0) {
      return "";
    }

    return tags
      .map((tag) => {
        const className = ["c_cat_item", tag.className || (tag.slug ? `--${tag.slug}` : "")]
          .filter(Boolean)
          .join(" ");
        const style = tag.color ? ` style="background-color:${escapeAttr(tag.color)}"` : "";

        return `<span class="${escapeAttr(className)}"${style}>${escapeHtml(tag.name)}</span>`;
      })
      .join("");
  }

  // --- ページャー ----------------------------------------------------------
  function renderPager(pagination) {
    // APIが返す現在ページと総ページ数から、前後ボタンとページ番号を生成する。
    const current = getPositiveInt(pagination?.current, 1);
    const totalPages = getPositiveInt(pagination?.totalPages, 1);
    const pages = getPagerPages(current, totalPages);
    const prev = current > 1 ? current - 1 : null;
    const next = current < totalPages ? current + 1 : null;

    return `
      <nav class="navigation pagination" aria-label="投稿ナビゲーション">
        <h2 class="screen-reader-text">投稿ナビゲーション</h2>
        <div class="nav-links">
          <ul class="page-numbers">
            ${
              prev
                ? `<li><a class="prev page-numbers" href="${escapeAttr(makePagedUrl(prev))}"><span class="ico"><img src="${escapeAttr(getAssetPath("/assets/img/ico_arrow_rounded_triangle_black_left.svg"))}" alt="前へ" /></span></a></li>`
                : ""
            }
            ${pages
              .map((page) => {
                if (page === "...") {
                  return '<li><span class="page-numbers dots">…</span></li>';
                }
                if (page === current) {
                  return `<li><span aria-current="page" class="page-numbers current">${page}</span></li>`;
                }
                return `<li><a class="page-numbers" href="${escapeAttr(makePagedUrl(page))}">${page}</a></li>`;
              })
              .join("")}
            ${
              next
                ? `<li><a class="next page-numbers" href="${escapeAttr(makePagedUrl(next))}"><span class="ico"><img src="${escapeAttr(getAssetPath("/assets/img/ico_arrow_rounded_triangle_black_right.svg"))}" alt="次へ" /></span></a></li>`
                : ""
            }
          </ul>
        </div>
      </nav>
    `;
  }

  function getPagerPages(current, totalPages) {
    // ページ数が多いときは、先頭・末尾・現在ページ周辺だけを残して省略記号を入れる。
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages, current - 1, current, current + 1]);
    const sorted = Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    return sorted.reduce((result, page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) {
        result.push("...");
      }
      result.push(page);
      return result;
    }, []);
  }

  // --- URL・API・表示用ユーティリティ ------------------------------------
  function makeDetailUrl(id) {
    // 静的プレビューでは detail.html、本番では既存URL互換の detail.php を使う。
    if (isStaticPreview()) {
      return `${state.rootPrefix}news/detail.html?pid=${encodeURIComponent(id)}`;
    }

    return `/news/detail.php?pid=${encodeURIComponent(id)}`;
  }

  function makePagedUrl(page) {
    // ページャーも静的プレビューと本番で同じように遷移できるURLを返す。
    const prefix = isStaticPreview() ? state.rootPrefix : "/";
    const path = `${prefix}news/`;

    return page <= 1 ? path : `${path}?paged=${encodeURIComponent(page)}`;
  }

  function updateCanonicalUrl(path) {
    // 静的テンプレートでは取得できないニュースのクエリ文字列を、canonical と OGP URL に反映する。
    const url = new URL(path, CANONICAL_ORIGIN).toString();
    const canonical = document.querySelector('link[rel="canonical"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');

    if (canonical) {
      canonical.href = url;
    }
    if (ogUrl) {
      ogUrl.content = url;
    }
  }

  async function fetchJson(url) {
    // APIレスポンスをJSONとして取得し、HTTPエラーも呼び出し元で扱える例外にする。
    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error?.message || `HTTP ${response.status}`);
    }

    return data;
  }

  function getApiBase() {
    if (NEWS_PROXY_BASE_URL && !NEWS_PROXY_BASE_URL.startsWith("__NEWS_PROXY")) {
      return `${NEWS_PROXY_BASE_URL.replace(/\/+$/, "")}/api/news`;
    }

    // /dist/ 以下で開く静的プレビューでは /dist/api/news を、
    // 公開環境では /api/news をAPIの起点として使う。
    return `${getRootPrefix()}api/news`.replace(/\/{2,}/g, "/");
  }

  function getRootPrefix() {
    // 現在のURLから /dist/ の有無を判定し、アセット・API共通のルートを返す。
    const pathname = window.location.pathname;
    const distIndex = pathname.indexOf("/dist/");
    if (distIndex !== -1) {
      return `${pathname.slice(0, distIndex)}/dist/`.replace(/\/{2,}/g, "/");
    }

    return "/";
  }

  function getAssetPath(path) {
    // サイト内アセットのパスを、現在の表示環境に合わせて組み立てる。
    return `${state.rootPrefix}${path.replace(/^\/+/, "")}`.replace(/\/{2,}/g, "/");
  }

  function getImageUrl(url) {
    // 外部WordPress画像はそのまま使い、相対パスや未設定画像は自サイト側を参照する。
    const value = url || DEFAULT_IMAGE;
    if (/^https?:\/\//.test(value)) {
      return value;
    }

    return getAssetPath(value);
  }

  function isStaticPreview() {
    // .html または /dist/ 配下なら、PHPを実行できない静的プレビューとみなす。
    return window.location.pathname.includes("/dist/") || window.location.pathname.endsWith(".html");
  }

  function getPositiveInt(value, fallback) {
    // クエリ文字列などを、安全な正の整数として扱うための共通処理。
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function escapeHtml(value) {
    // API由来のタイトル・タグなどをHTMLへ埋め込む前にエスケープする。
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function escapeAttr(value) {
    return escapeHtml(value);
  }
})();
